import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QrCode, ChefHat, ArrowRight, Utensils, Phone, Clock, MapPin, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { fileUrl } from "@/lib/api";
import { PageState } from "@/components/ui/page-state";

export default function LandingPage() {
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadOutlets = useCallback(async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("/outlets/public");
        setOutlets(data || []);
      } catch {
        setError("Informasi outlet belum dapat dimuat. Silakan periksa koneksi Anda.");
      } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadOutlets(); }, [loadOutlets]);

  const primaryOutlet = outlets[0];
  const logoOf = (o) => o?.logo_file_id ? fileUrl(o.logo_file_id) : (o?.logo_url || "");
  const heroSrc = primaryOutlet?.header_image_file_id ? fileUrl(primaryOutlet.header_image_file_id)
    : primaryOutlet?.header_image_url || (primaryOutlet?.login_image_file_id ? fileUrl(primaryOutlet.login_image_file_id) : primaryOutlet?.login_image_url || "");
  // Status buka/tutup dihitung backend dari jam buka outlet (+ toggle manual).
  const isOutletClosed = (o) => o?.is_open_now === false || (o?.is_open_now === undefined && o?.menu_open === false);

  return (
    <div className="min-h-screen bg-background">
      <nav aria-label="Navigasi utama" className="max-w-6xl mx-auto flex items-center justify-between gap-4 px-4 md:px-6 py-5 md:py-6 border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-white border border-border grid place-items-center overflow-hidden flex-shrink-0">
            {logoOf(primaryOutlet) ? (
              <img src={logoOf(primaryOutlet)} alt="logo" className="w-full h-full object-contain p-1"/>
            ) : (
              <Utensils className="w-5 h-5 text-primary"/>
            )}
          </div>
          <span className="font-display font-semibold text-xl md:text-2xl tracking-tight truncate" data-testid="brand-name">
            {primaryOutlet?.name || "Meja&Menu"}
          </span>
        </div>
        <Link to="/admin/login">
          <Button variant="outline" size="sm" data-testid="admin-login-btn" className="rounded-full">
            <span className="hidden sm:inline">Admin Login</span>
            <span className="sm:hidden">Admin</span>
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </nav>

      <main>
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-20 grid md:grid-cols-2 gap-8 md:gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-brass/40 bg-secondary/60 px-3 py-1.5 text-[11px] font-semibold text-secondary-foreground mb-4 md:mb-6">
            <Utensils className="h-3.5 w-3.5" />
            Sajian pilihan · Pemesanan QR
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.05] text-teak">
            Scan meja. <br />
            Pesan. <span className="text-heritage">Nikmati.</span>
          </h1>
          <p className="mt-4 md:mt-6 text-base md:text-lg text-muted-foreground max-w-lg">
            Pilih sajian favorit dari meja atau kamar Anda. Scan QR, kirim pesanan, dan biarkan dapur kami menyiapkannya untuk Anda.
          </p>
          <div className="mt-6 md:mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-full bg-heritage hover:bg-teak"><a href="#outlets">Jelajahi Outlet <ArrowRight className="h-4 w-4" /></a></Button>
            <Link to="/admin/login">
              <Button size="lg" variant="outline" className="rounded-full h-11 md:h-12 px-5 md:px-6" data-testid="hero-admin-btn">
                <ChefHat className="w-4 h-4 mr-2" /> Dashboard Admin
              </Button>
            </Link>
          </div>
        </div>
        <div className="relative overflow-hidden rounded-[2rem] bg-accent border border-border shadow-warm-lg">
          {heroSrc ? <img src={heroSrc} alt={primaryOutlet?.name || "Restoran"} className="object-cover w-full aspect-[4/3] md:aspect-square" /> : (
            <div className="grid aspect-[4/3] md:aspect-square place-items-center text-brass">{logoOf(primaryOutlet) ? <img src={logoOf(primaryOutlet)} alt={primaryOutlet.name} className="h-28 w-28 object-contain" /> : <Utensils className="h-20 w-20" />}</div>
          )}
          {primaryOutlet && <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-teak/90 to-transparent px-6 pb-6 pt-16 text-white"><p className="font-display text-3xl font-semibold">{primaryOutlet.name}</p>{primaryOutlet.tagline && <p className="mt-1 text-sm text-white/90">{primaryOutlet.tagline}</p>}</div>}
        </div>
      </section>

      {/* Outlets */}
      <section id="outlets" aria-busy={loading} className="max-w-6xl mx-auto px-4 md:px-6 pb-16 md:pb-24 scroll-mt-8">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-3xl md:text-4xl font-semibold flex items-center gap-2">
            <Store className="w-5 h-5 text-primary" /> Outlet Kami
          </h2>
          {!loading && !error && <span className="text-xs text-muted-foreground">{outlets.length} cabang</span>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {loading ? <PageState kind="loading" title="Memuat outlet" className="col-span-full" /> : error ? <PageState kind="error" title="Outlet belum dapat dimuat" description={error} onRetry={loadOutlets} className="col-span-full" /> : outlets.length === 0 ? <PageState title="Belum ada outlet aktif" description="Silakan kembali lagi nanti." className="col-span-full" /> : null}
          {!loading && !error && outlets.map((o) => (
            <div key={o.id} data-testid={`outlet-card-${o.slug}`}
                 className="surface-card p-5 md:p-6 hover:-translate-y-1 transition-transform duration-200">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-xl bg-accent border border-border overflow-hidden grid place-items-center flex-shrink-0">
                  {logoOf(o) ? (
                    <img src={logoOf(o)} alt={o.name} className="w-full h-full object-contain p-1"/>
                  ) : (
                    <Utensils className="w-6 h-6 text-primary"/>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <h3 className="font-display font-bold text-lg md:text-xl">{o.name}</h3>
                    <span
                      className={`mt-0.5 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium flex-shrink-0 ${
                        isOutletClosed(o) ? "bg-warning/15 text-warning" : "bg-primary/10 text-primary"
                      }`}
                      data-testid={`outlet-open-badge-${o.slug}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${isOutletClosed(o) ? "bg-warning" : "bg-primary"}`}/>
                      {isOutletClosed(o) ? "Tutup" : "Buka"}
                    </span>
                  </div>
                  {o.tagline && <p className="text-sm text-muted-foreground line-clamp-1">{o.tagline}</p>}
                  <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                    {o.phone && <p className="inline-flex items-center gap-1"><Phone className="w-3 h-3"/>{o.phone}</p>}
                    {o.hours && <p className="flex items-center gap-1 mt-1"><Clock className="w-3 h-3 shrink-0"/>{o.hours}</p>}
                    {o.address && <p className="inline-flex items-center gap-1 mt-1"><MapPin className="w-3 h-3"/>{o.address}</p>}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <Link to={`/order?outlet=${o.slug}&tableNumber=12`}>
                  <Button className="w-full rounded-full" data-testid={`try-outlet-${o.slug}-btn`}>
                    <QrCode className="w-4 h-4 mr-2"/> Coba Demo — Meja 12
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>
      </main>
      <footer className="border-t border-border px-4 py-6 text-center text-xs text-muted-foreground">{primaryOutlet?.name || "Meja&Menu"} · Selamat menikmati sajian Anda.</footer>
    </div>
  );
}
